import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import { updateOrderStatus, clearError, fetchAdminOrderById } from '../../store/slices/adminSlice';
import { Order, OrderStatus } from '../../types';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice } from '../../utils/currency';

const orderStatusOptions: { value: OrderStatus; label: string; color: any }[] = [
  { value: 'PENDING', label: 'Pending', color: 'warning' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'info' },
  { value: 'PROCESSING', label: 'Processing', color: 'primary' },
  { value: 'SHIPPED', label: 'Shipped', color: 'secondary' },
  { value: 'DELIVERED', label: 'Delivered', color: 'success' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'error' },
  { value: 'RETURNED', label: 'Returned', color: 'default' },
];

const OrderDetail: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { orders, error } = useSelector((state: RootState) => state.admin);
  const { currencySettings } = useSettings();
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (id) {
      // Try to find order in existing orders first
      const foundOrder = orders.find(o => o.id === parseInt(id));
      if (foundOrder && foundOrder.items && foundOrder.items.length > 0) {
        // Order already has full data
        setOrder(foundOrder);
      } else {
        // Fetch full order details from API
        dispatch(fetchAdminOrderById(parseInt(id)) as any);
      }
    }
  }, [id, dispatch, orders]);

  useEffect(() => {
    if (id) {
      const foundOrder = orders.find(o => o.id === parseInt(id));
      if (foundOrder) {
        setOrder(foundOrder);
      }
    }
  }, [id, orders]);

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (order) {
      try {
        await dispatch(updateOrderStatus({ orderId: order.id, status: newStatus }) as any);
        setOrder({ ...order, status: newStatus });
        toast.success('Order status updated successfully');
      } catch (error) {
        toast.error('Failed to update order status');
      }
    }
  };

  const formatPriceWithSettings = (price: number | undefined | null) => {
    if (price == null) return formatPrice(0, currencySettings);
    return formatPrice(price, currencySettings);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    const statusOption = orderStatusOptions.find(opt => opt.value === status);
    return statusOption?.color || 'default';
  };

  if (!order) {
    return (
      <Box>
        <Typography variant="h5">Order not found</Typography>
        <Button onClick={() => navigate('/admin/orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/admin/orders"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/orders');
          }}
        >
          Orders
        </Link>
        <Typography color="text.primary">
          Order #{order.orderNumber}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/orders')}
        >
          Back to Orders
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Order Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Order Information</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={order.status}
                    onChange={(e) => handleStatusChange(e.target.value as OrderStatus)}
                    label="Status"
                  >
                    {orderStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Order Number</Typography>
                  <Typography variant="h6">{order.orderNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Order Date</Typography>
                  <Typography variant="h6">{formatDate(order.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={order.status}
                    color={getStatusColor(order.status)}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Payment Method</Typography>
                  <Typography variant="h6">{order.paymentMethod?.replace('_', ' ') || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              component="img"
                              sx={{
                                height: 40,
                                width: 40,
                                borderRadius: 1,
                                objectFit: 'cover',
                              }}
                              src={item.product.imageUrl || '/placeholder-book.svg'}
                              alt={item.product.title}
                              onError={(e: any) => {
                                e.target.src = '/placeholder-book.svg';
                              }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.product.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {item.product.author}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatPriceWithSettings(item.price)}</TableCell>
                        <TableCell align="right">{formatPriceWithSettings(item.totalPrice)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body1">Subtotal</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {formatPriceWithSettings(
                            order.items?.reduce((sum, item) => sum + (item.totalPrice || 0), 0) || 0
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body1">Shipping</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {order.shippingCost === null
                            ? 'В зависимост от тарифата на куриера'
                            : order.shippingCost === 0
                            ? 'Безплатна доставка!'
                            : formatPriceWithSettings(order.shippingCost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="h6">Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">{formatPriceWithSettings(order.totalAmount)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Customer Information</Typography>
              <Typography variant="body1" fontWeight="medium">
                {order.user ? `${order.user.firstName} ${order.user.lastName}` : order.guestName || 'Guest Customer'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {order.user ? order.user.email : order.guestEmail || 'Guest Order'}
              </Typography>
              {(order.user?.phoneNumber || order.guestPhone) && (
                <Typography variant="body2" color="textSecondary">
                  {order.user?.phoneNumber || order.guestPhone}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Shipping Address</Typography>
              <Typography variant="body2">
                {order.shippingAddress?.address || 'N/A'}<br />
                {order.shippingAddress?.city || 'N/A'} {order.shippingAddress?.postalCode || ''}<br />
                {order.shippingAddress?.country || 'N/A'}
              </Typography>
            </CardContent>
          </Card>

          {order.notes && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Order Notes</Typography>
                <Typography variant="body2">
                  {order.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetail;
